import { useRef, useState } from 'react'
import {
  formatMoney, formatMiles, lowestPrice, relativeTime, mapsUrl, ageLabel,
  STATUSES, STATUS_LABELS, dealerKey, LINK_HEALTH_INFO, isFollowUpDue,
} from '../util.js'

// '' is a real saved value (no status); null clears a number. Empty text
// inputs become null too, so clearing a field actually clears it.
const numOrNull = (v) => (v === '' || v == null ? null : Number(v))
const textOrNull = (v) => (v === '' || v == null ? null : v)

const bikeStateFrom = (note = {}) => ({
  status: note.status || '',
  notes: note.notes || '',
  tradeValue: note.tradeValue ?? '',
  otdQuote: note.otdQuote ?? '',
  lastContactAt: note.lastContactAt || '',
  followUpAt: note.followUpAt || '',
})

const contactStateFrom = (dealer = {}) => ({
  salesperson: dealer.salesperson || '',
  phone: dealer.phone || '',
  email: dealer.email || '',
  notes: dealer.notes || '',
})

// How each draft field is converted for the write. status is deliberately not
// run through textOrNull: '' is a real saved value there (no status), where
// for every other field an emptied input means "clear this".
const BIKE_SEND = {
  status: (v) => v,
  notes: textOrNull,
  tradeValue: numOrNull,
  otdQuote: numOrNull,
  lastContactAt: textOrNull,
  followUpAt: textOrNull,
}
const CONTACT_SEND = {
  salesperson: textOrNull, phone: textOrNull, email: textOrNull, notes: textOrNull,
}

// The bike fields that live behind the More expander. A failure naming one of
// them has to force the group open, or the operator is told something failed
// about a field that isn't on screen.
const BIKE_MORE_FIELDS = ['tradeValue', 'otdQuote', 'lastContactAt', 'followUpAt']

// A DOM input's .value is always a string, so a freshly-typed draft holds
// "4200" for tradeValue/otdQuote. RTDB round-trips a saved number as a
// number, so once the listener's `note` prop updates after a save,
// bikeStateFrom(note) holds 4200 (a number) for the very same value. A raw
// JSON.stringify comparison sees "4200" !== 4200 and reads that as a change,
// prompting "discard unsaved changes?" on a backdrop click when nothing is
// actually unsaved. Normalising both sides through the same string coercion
// before comparing closes that gap without touching what gets sent on save
// (BIKE_SEND still does that conversion).
const normalizeBikeState = (state) => ({
  ...state,
  tradeValue: state.tradeValue === '' || state.tradeValue == null ? '' : String(state.tradeValue),
  otdQuote: state.otdQuote === '' || state.otdQuote == null ? '' : String(state.otdQuote),
})
const asIs = (x) => x
const sameBike = (a, b) => JSON.stringify(normalizeBikeState(a)) === JSON.stringify(normalizeBikeState(b))
const sameContact = (a, b) => JSON.stringify(a) === JSON.stringify(b)

// The payload for a save: ONLY the fields whose value actually differs from
// the seed the draft was built from.
//
// Sending the whole object instead is what made a save destructive. clean() in
// workspace.js passes null straight through, and an RTDB update() with a null
// value DELETES that key — so every untouched-but-empty field in the payload
// was a delete. Two ways that bit: a save the operator meant as "just the
// salesperson" also wrote status:'' and notes:null over a note another
// session had just changed, and a dealer record corrected elsewhere while the
// sheet sat open was reverted, with any field added in that same remote edit
// deleted outright. The dealer record in particular is hand-typed, shared
// across every bike from that dealer, and exists nowhere else — no crawl can
// put it back. A diff cannot express either mistake: a field the operator
// never touched is not in the payload at all.
const patchFrom = (draft, seed, senders, normalize = asIs) => {
  const d = normalize(draft)
  const s = normalize(seed)
  return Object.fromEntries(
    Object.keys(senders).filter((k) => d[k] !== s[k]).map((k) => [k, senders[k](draft[k])])
  )
}

// Adopt a record that changed underneath the sheet, field by field: a field
// the operator has not touched takes the new value, one he is mid-edit on
// keeps his. Wholesale reseeding would wipe in-progress typing (which is why
// the reseed effects were keyed narrowly in the first place); not reseeding at
// all left the draft silently diverging from the record it claims to show.
const mergeDraft = (draft, prevSeed, nextSeed, normalize = asIs) => {
  const d = normalize(draft)
  const p = normalize(prevSeed)
  const out = { ...draft }
  for (const k of Object.keys(nextSeed)) if (d[k] === p[k]) out[k] = nextSeed[k]
  return out
}

export default function ListingDetail({
  listing, note = {}, dealer = {}, onClose, onSaveNote, onSaveDealer,
}) {
  const [bike, setBike] = useState(() => bikeStateFrom(note))
  const [contact, setContact] = useState(() => contactStateFrom(dealer))
  // Controlled, not a bare <details> — the save bar (Task 4) needs to force
  // this open programmatically when a save fails on a field it hides.
  const [showMore, setShowMore] = useState(false)
  // Which identifier's copy just failed, so the failure is visible rather
  // than silent. Cleared on the next successful copy of either field.
  const [copyFailed, setCopyFailed] = useState(null)
  // null | 'saving' | 'saved' | { errors: [{ half, message }] } — the one
  // save bar's state. Cleared back to null on the next field change so a
  // stale "Saved" (or a stale failure) never sits above fresh edits.
  const [saveState, setSaveState] = useState(null)
  // The photo is hotlinked from MotoHunt's CDN, so it will rot — a failed
  // load must render as nothing, never a broken-image icon or a collapsed
  // layout hole. Reset alongside the listing.id reseed below, or one bike's
  // dead photo would keep hiding the next bike's good one.
  const [photoBroken, setPhotoBroken] = useState(false)

  const copyToClipboard = (value, label) => async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopyFailed(null)
    } catch {
      setCopyFailed(label)
    }
  }

  // The drafts as they stand right now, for the one reader that cannot use
  // the render's own values: the async tail of saveAll, which resolves a
  // round-trip after the click that captured them.
  const bikeRef = useRef(bike)
  const contactRef = useRef(contact)
  bikeRef.current = bike
  contactRef.current = contact
  // Which bike this render is for, for the same reason: a write can land
  // after the sheet has been pointed somewhere else.
  const listingIdRef = useRef(listing.id)
  listingIdRef.current = listing.id

  // Keyed on the seeded VALUES, not on the note/dealer objects: a parent that
  // rebuilds those objects on every render (App.jsx does) would otherwise
  // reseed on every keystroke and wipe in-progress typing. Built through
  // bikeStateFrom / contactStateFrom rather than off the raw records, so a
  // field the sheet does not own — the crawler's /dealers/<key>/sourced
  // block, rewritten on every daily run — cannot trigger a reseed at all.
  const noteKey = JSON.stringify(normalizeBikeState(bikeStateFrom(note)))
  const contactKey = JSON.stringify(contactStateFrom(dealer))
  const dealerSlug = dealerKey(listing.dealerName)

  // What each draft was last seeded from — the version of the record the
  // operator's edits are relative to. Two jobs:
  //
  //  - it is the "before" the merge below diffs against, which is how an edit
  //    to one field can be kept while every other field takes the value a
  //    remote change just gave it. Without that, a record changed under an
  //    open sheet left the whole draft stale, the sheet read "Unsaved
  //    changes" with nothing typed, and the next save reverted the change.
  //  - it is the baseline the save payload and the dirty flags are measured
  //    against. Once the adjustment below has run it holds exactly the
  //    current record, so this is the same comparison as against the prop —
  //    written this way because the payload is a diff against what the sheet
  //    was showing, which is the thing that has to stay true.
  //
  // Reseeding happens during render rather than in an effect, and this is
  // load-bearing rather than stylistic: an effect runs after the render that
  // already computed the payload and the dirty flags, so for one commit the
  // sheet would measure a new record against the old seed. That commit is
  // exactly where the backdrop-close confirm and the Save button read their
  // state from. Adjusting state during render is React's documented answer to
  // "a prop changed and some state derived from it must change with it": the
  // re-render happens before anything is committed or shown.
  const [seeds, setSeeds] = useState(() => ({
    id: listing.id,
    slug: dealerSlug,
    bike: bikeStateFrom(note),
    contact: contactStateFrom(dealer),
  }))

  if (seeds.id !== listing.id) {
    // A different bike is a hard reseed, not a merge — there is no edit worth
    // preserving across it, and keeping one would let the operator save one
    // bike's notes onto another.
    //
    // Everything else the sheet remembers is per-bike too. App.jsx renders it
    // without a key and the push-notification handler swaps openId under an
    // already-mounted sheet, so a saveState left behind reports the previous
    // bike's outcome over this one: "Saved" for a bike never saved, or a
    // permission-denied alert over a Save button that is (correctly) disabled
    // because this bike is clean, which nothing but typing would clear.
    const seed = bikeStateFrom(note)
    setSeeds((s) => ({ ...s, id: listing.id, bike: seed }))
    setBike(seed)
    setPhotoBroken(false)
    setSaveState(null)
    setShowMore(false)
    setCopyFailed(null)
  } else if (noteKey !== JSON.stringify(normalizeBikeState(seeds.bike))) {
    const next = bikeStateFrom(note)
    setSeeds((s) => ({ ...s, bike: next }))
    setBike(mergeDraft(bike, seeds.bike, next, normalizeBikeState))
  }

  if (seeds.slug !== dealerSlug) {
    // A different dealer is a different shared record; the same hard-reseed
    // argument applies.
    const seed = contactStateFrom(dealer)
    setSeeds((s) => ({ ...s, slug: dealerSlug, contact: seed }))
    setContact(seed)
  } else if (contactKey !== JSON.stringify(seeds.contact)) {
    const next = contactStateFrom(dealer)
    setSeeds((s) => ({ ...s, contact: next }))
    setContact(mergeDraft(contact, seeds.contact, next))
  }

  const field = (set) => (e) => {
    // Left alone while a write is in flight: 'saving' is what disables the
    // Save button, and clearing it on a keystroke let a second Save go out
    // over the first. Firebase's update() does not reject when offline — it
    // stays pending — so that window is as long as the signal is bad.
    setSaveState((s) => (s === 'saving' ? s : null))
    set((s) => ({ ...s, [e.target.name]: e.target.value }))
  }
  const maps = mapsUrl(listing)
  // 'unchecked' is a display-only fallback, not a crawler-written state (see
  // LINK_HEALTH_STATES) — without it a never-checked link rendered nothing
  // here at all, indistinguishable from a healthy one, in the one view where
  // the operator is deciding whether to go check the link personally.
  const healthState = listing.linkHealth?.state || 'unchecked'

  // Phase 2b fields — absent on every pre-2b listing (the common case until
  // the crawler backfills), so each of these degrades to something that
  // renders rather than throwing or leaving a gap.
  const specs = listing.specs || {}
  const heading = specs.Model || `${listing.year ?? ''} ${listing.trim ?? ''}`.trim()
  // specs values are source-controlled text straight from MotoHunt's HTML —
  // filter(Boolean) is what keeps a partial set from producing a line with
  // dangling " · " separators when only some values are present.
  const specLine = [specs.Color, specs.Condition, specs.Engine].filter(Boolean).join(' · ')
  // The operator's typed number is one he has verified (may be a
  // salesperson's direct line rather than the switchboard MotoHunt lists);
  // the crawler-sourced one is one nobody has dialled yet, so his always
  // wins and the sourced one is labelled when shown on its own.
  const callNumber = dealer.phone || dealer.sourced?.phone || null
  const callIsSourced = !dealer.phone && !!dealer.sourced?.phone

  // What each half would send, and therefore whether it has anything to send
  // at all. Split in two — not just OR'd into one isDirty — because each half
  // is a separate write: a bike-only save must not touch the dealer record,
  // and a dealer-only save must not touch the note.
  //
  // saveDealer rejects when there is no dealer name, or when the name slugs to
  // the shared 'unknown' bucket (one seller's phone number would land on
  // another's bike). canSaveDealer mirrors that guard and gates BOTH the write
  // and the rendering of the contact fields, so the sheet never offers an
  // action that cannot succeed and never counts one as unsaved work.
  const bikePatch = patchFrom(bike, seeds.bike, BIKE_SEND, normalizeBikeState)
  const contactPatch = patchFrom(contact, seeds.contact, CONTACT_SEND)
  const canSaveDealer = !!listing.dealerName && dealerSlug !== 'unknown'
  const bikeDirty = Object.keys(bikePatch).length > 0
  const dealerDirty = canSaveDealer && Object.keys(contactPatch).length > 0
  const isDirty = bikeDirty || dealerDirty

  // The backdrop fills most of a phone screen below a short sheet (see
  // .sheet-backdrop's `align-items: flex-start`), and this is the one place
  // in the app the operator types. A stray tap outside the sheet must not
  // throw away an in-progress edit the way the explicit × control is allowed
  // to (that one stays a plain, unconfirmed close either way).
  const closeBackdrop = () => {
    if (isDirty && !window.confirm('Discard unsaved changes?')) return
    onClose?.()
  }

  const saveAll = async () => {
    // Everything this save reports on is captured here, at the click, and
    // compared against the live drafts when the write resolves — a round trip
    // later, by which time the operator may have typed again or the sheet may
    // be showing a different bike.
    const forId = listing.id
    const sentBike = bikePatch
    const sentContact = contactPatch
    const bikeAtSend = bike
    const contactAtSend = contact

    setSaveState('saving')
    const jobs = []
    // Each half is only written when it actually has a change to write. A
    // no-op update() is not harmless here: it resends the draft, and a draft
    // that has drifted from the record would resend stale values.
    if (bikeDirty) jobs.push({ half: 'bike', run: () => onSaveNote?.(forId, sentBike) })
    if (dealerDirty) jobs.push({ half: 'dealer', run: () => onSaveDealer?.(listing.dealerName, sentContact) })

    const results = await Promise.allSettled(jobs.map((j) => j.run()))

    // The sheet moved to another bike while this was in flight. Reporting
    // this bike's outcome over that bike's form would be reporting on a save
    // the operator never made there; the listing.id effect has already reset
    // the bar for the new bike.
    if (listingIdRef.current !== forId) return

    const errors = results
      .map((r, i) => (r.status === 'rejected' ? { half: jobs[i].half, message: r.reason?.message } : null))
      .filter(Boolean)

    if (errors.length) {
      // Never report a failure about a field the operator cannot see. Every
      // dealer field lives inside More; on the bike half only some do, so
      // that half opens the group only when the failed write actually carried
      // one of them.
      const hidden = errors.some((e) =>
        e.half === 'dealer' ||
        (e.half === 'bike' && BIKE_MORE_FIELDS.some((f) => f in sentBike)))
      if (hidden) setShowMore(true)
      setSaveState({ errors })
      return
    }

    // "Saved" is a claim about what is on screen, not about what was sent. An
    // edit made during the round trip is not in that write — saying "Saved"
    // over it invites the operator to close the sheet on an unsaved change,
    // and the × control closes without confirming.
    const diverged =
      !sameBike(bikeRef.current, bikeAtSend) || !sameContact(contactRef.current, contactAtSend)
    setSaveState(diverged ? null : 'saved')
  }

  return (
    <div className="sheet-backdrop" onClick={closeBackdrop}>
      <div className="sheet" role="dialog" aria-label="Listing detail" onClick={(e) => e.stopPropagation()}>
        {listing.photoUrl && !photoBroken && (
          <img
            className="listing-photo"
            src={listing.photoUrl}
            alt={heading}
            onError={() => setPhotoBroken(true)}
          />
        )}
        <header className="sheet-head">
          <h2>{heading}</h2>
          <button className="close" onClick={onClose} aria-label="Close">×</button>
        </header>

        <section className="sheet-facts">
          <p className="price">
            {formatMoney(listing.price)}
            {specs['Base MSRP'] && <span className="msrp"> · MSRP {specs['Base MSRP']}</span>}
          </p>
          <p className="muted">
            {formatMiles(listing.mileage)} · {listing.location}
            {ageLabel(listing) ? ` · ${ageLabel(listing)}` : ''}
          </p>
          {specLine && <p className="spec-line">{specLine}</p>}
          {callNumber && (
            <p className="call-line">
              <a href={`tel:${callNumber}`}>{callNumber}</a>
              {callIsSourced && <span className="muted"> (from the listing)</span>}
            </p>
          )}
          <p className="links">
            {listing.dealerUrl ? (
              <a href={listing.dealerUrl} target="_blank" rel="noreferrer">Dealer ↗</a>
            ) : (
              listing.listingUrl && (
                <a className="muted" href={listing.listingUrl} target="_blank" rel="noreferrer">
                  MotoHunt ↗
                </a>
              )
            )}
            {maps && <a href={maps} target="_blank" rel="noreferrer">Map ↗</a>}
          </p>
          <dl className="ids">
            {listing.stockNumber && (
              <div>
                <dt>Stock</dt>
                <dd className="stock">
                  <span>{listing.stockNumber}</span>
                  <button
                    type="button"
                    className="copy-btn"
                    aria-label="Copy stock number"
                    onClick={copyToClipboard(listing.stockNumber, 'stock number')}
                  >
                    Copy
                  </button>
                </dd>
              </div>
            )}
            {listing.vin && (
              <div>
                <dt>VIN</dt>
                <dd className="vin">
                  <span>{listing.vin}</span>
                  <button
                    type="button"
                    className="copy-btn"
                    aria-label="Copy VIN"
                    onClick={copyToClipboard(listing.vin, 'VIN')}
                  >
                    Copy
                  </button>
                </dd>
              </div>
            )}
          </dl>
          {copyFailed && (
            <p className="err" role="alert">
              Couldn't copy the {copyFailed} — copy it manually.
            </p>
          )}
          <p className={`health-line h-${healthState}`}>
            {LINK_HEALTH_INFO[healthState]?.text}
            {listing.linkHealth?.checkedAt && ` · checked ${relativeTime(listing.linkHealth.checkedAt)}`}
          </p>
        </section>

        <section>
          <label>
            Status
            <select name="status" value={bike.status} onChange={field(setBike)}>
              <option value="">Not seen</option>
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
          </label>
          {bike.followUpAt && (
            <p className={`followup-line${isFollowUpDue(bike) ? ' due' : ''}`}>
              Follow up {isFollowUpDue(bike) ? 'due' : ''} {bike.followUpAt}
            </p>
          )}
          <label>Notes
            <textarea name="notes" rows={4} value={bike.notes} onChange={field(setBike)} />
          </label>
        </section>

        <button
          type="button"
          className="more-toggle"
          aria-expanded={showMore}
          onClick={() => setShowMore((v) => !v)}
        >
          {showMore ? 'Less' : 'More'}
        </button>

        {showMore && (
          <>
            <section className="more-group">
              <h3>This bike</h3>
              <div className="field-pair">
                <label>Trade value
                  <input name="tradeValue" type="number" value={bike.tradeValue} onChange={field(setBike)} />
                </label>
                <label>Out-the-door quote
                  <input name="otdQuote" type="number" value={bike.otdQuote} onChange={field(setBike)} />
                </label>
              </div>
              <div className="field-pair">
                <label>Last contact
                  <input name="lastContactAt" type="date" value={bike.lastContactAt} onChange={field(setBike)} />
                </label>
                <label>Follow up
                  <input name="followUpAt" type="date" value={bike.followUpAt} onChange={field(setBike)} />
                </label>
              </div>
            </section>

            {canSaveDealer && (
              <section className="more-group">
                <h3>{listing.dealerName}</h3>
                <p className="hint">Shared across all {listing.dealerName} listings.</p>
                <label>Salesperson
                  <input name="salesperson" value={contact.salesperson} onChange={field(setContact)} />
                </label>
                <label>Phone
                  <input name="phone" value={contact.phone} onChange={field(setContact)} />
                </label>
                <label>Email
                  <input name="email" value={contact.email} onChange={field(setContact)} />
                </label>
                <label>Dealer notes
                  <textarea name="notes" rows={3} value={contact.notes} onChange={field(setContact)} />
                </label>
              </section>
            )}
          </>
        )}

        <section>
          <h3>Price history</h3>
          {listing.priceHistory?.length ? (
            <>
              <ul className="history">
                {listing.priceHistory.map((h, i) => (
                  <li key={i}>{formatMoney(h.price)} <span className="muted">{relativeTime(h.seenAt)}</span></li>
                ))}
              </ul>
              <p className="muted">Lowest seen: {formatMoney(lowestPrice(listing))}</p>
            </>
          ) : (
            <p className="muted">No price recorded.</p>
          )}
        </section>

        <div className="save-bar">
          {saveState?.errors ? (
            <p className="err" role="alert">
              {saveState.errors
                .map((e) => `${e.half === 'bike' ? 'Bike' : 'Dealer'} not saved — ${e.message}`)
                .join(' · ')}
            </p>
          ) : (
            <span className="save-state">
              {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : isDirty ? 'Unsaved changes' : ''}
            </span>
          )}
          <button className="primary" onClick={saveAll} disabled={!isDirty || saveState === 'saving'}>Save</button>
        </div>
      </div>
    </div>
  )
}
